import React from "react";
import {
  getAffiliateBonusState,
  getTotalVisibleBonus,
  getWithdrawableBalance,
} from "../lib/affiliateBonus";

function StatRow({ label, value, valueClassName = "text-white" }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-slate-300">{label}</span>
      <span className={`font-bold ${valueClassName}`}>{value}</span>
    </div>
  );
}

export default function BonusModal({ user, onClose }) {
  const depositBonus = Number(user?.bonusAmount ?? user?.depositBonus ?? 0);
  const turnoverRequired = Number(user?.turnoverRequired ?? 0);
  const turnoverCompleted = Number(user?.turnoverCompleted ?? 0);
  const depositAmount = Number(user?.depositAmount ?? 0);
  const balance = Number(user?.balance ?? 0);
  const affiliateState = getAffiliateBonusState(user);
  const totalBonus = getTotalVisibleBonus(user);
  const withdrawableBalance = getWithdrawableBalance(user);

  const depositProgress =
    turnoverRequired > 0
      ? Math.min((turnoverCompleted / turnoverRequired) * 100, 100)
      : 0;
  const affiliateProgress =
    affiliateState.required > 0
      ? Math.min((affiliateState.completed / affiliateState.required) * 100, 100)
      : 0;

  if (totalBonus < 0.01) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 px-4">
        <div className="bg-slate-900 rounded-2xl shadow-2xl p-8 max-w-md w-full flex flex-col items-center">
          <div className="text-yellow-400 text-xl font-bold mb-4">
            No bonus active right now.
          </div>
          <button
            className="mt-3 px-8 py-2 rounded-full bg-slate-700 text-lime-200 font-bold"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 px-4">
      <div className="bg-[#1b2538] rounded-2xl shadow-2xl p-8 max-w-2xl w-full border-2 border-lime-300/20">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-lime-200 text-2xl font-extrabold flex items-center gap-2">
              <span role="img" aria-label="gift">
                🎁
              </span>
              Bonus Wallet
            </div>
            <div className="mt-2 text-sm text-slate-300">
              Total visible bonus: {totalBonus.toFixed(2)} USDT
            </div>
          </div>
          <button
            className="px-5 py-2 rounded-full bg-slate-700 text-lime-200 font-bold"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-5">
            <div className="text-lg font-black text-white">Deposit bonus</div>
            {depositBonus > 0 && turnoverRequired > 0 ? (
              <>
                <div className="mt-4 space-y-2">
                  <StatRow label="Locked bonus" value={`+${depositBonus.toFixed(2)} USDT`} valueClassName="text-lime-300" />
                  <StatRow label="Deposit amount" value={`${depositAmount.toFixed(2)} USDT`} />
                  <StatRow label="Turnover" value={`${turnoverCompleted.toFixed(0)} / ${turnoverRequired.toFixed(0)}`} />
                </div>
                <div className="mt-4 h-4 rounded-full bg-slate-700">
                  <div
                    className="h-4 rounded-full bg-gradient-to-r from-yellow-300 via-lime-300 to-green-400"
                    style={{ width: `${depositProgress}%` }}
                  />
                </div>
                <div className="mt-2 text-xs text-lime-200">{depositProgress.toFixed(2)}% complete</div>
              </>
            ) : (
              <div className="mt-4 text-sm text-slate-400">No active deposit bonus.</div>
            )}
          </div>

          <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-5">
            <div className="text-lg font-black text-white">Affiliate bonus</div>
            {affiliateState.locked > 0 || affiliateState.unlocked > 0 ? (
              <>
                <div className="mt-4 space-y-2">
                  <StatRow label="Locked" value={`${affiliateState.locked.toFixed(2)} USDT`} valueClassName="text-amber-300" />
                  <StatRow label="Unlocked" value={`${affiliateState.unlocked.toFixed(2)} USDT`} valueClassName="text-emerald-300" />
                  <StatRow
                    label="Turnover"
                    value={affiliateState.required > 0 ? `${affiliateState.completed.toFixed(0)} / ${affiliateState.required.toFixed(0)}` : "Unlocked"}
                  />
                </div>
                {affiliateState.required > 0 ? (
                  <>
                    <div className="mt-4 h-4 rounded-full bg-slate-700">
                      <div
                        className="h-4 rounded-full bg-gradient-to-r from-orange-300 via-amber-300 to-emerald-400"
                        style={{ width: `${affiliateProgress}%` }}
                      />
                    </div>
                    <div className="mt-2 text-xs text-amber-100">{affiliateProgress.toFixed(2)}% complete</div>
                  </>
                ) : (
                  <div className="mt-4 text-sm font-bold text-emerald-300">
                    Affiliate rewards unlocked and ready to withdraw.
                  </div>
                )}
              </>
            ) : (
              <div className="mt-4 text-sm text-slate-400">No affiliate rewards yet.</div>
            )}
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/40 p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm text-slate-300">Wallet balance</div>
              <div className="text-2xl font-black text-white">{balance.toFixed(2)} USDT</div>
            </div>
            <div>
              <div className="text-sm text-slate-300">Available to withdraw</div>
              <div className="text-2xl font-black text-lime-300">{withdrawableBalance.toFixed(2)} USDT</div>
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-400">
            Locked affiliate rewards stay in wallet balance, but they are not withdrawable until their turnover requirement is completed.
          </div>
        </div>
      </div>
    </div>
  );
}
